import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import { from, switchMap } from 'rxjs';
import { LearningPathFullModel } from 'src/app/models/learning-paths/learning-path.model';
import { PersonModel } from 'src/app/models/person/person.model';
import { RawFileModel } from 'src/app/models/raw-file-model';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { TrainingMessageSendModel } from 'src/app/models/training/training-message-send.model';
import { FileUtilService } from 'src/app/services/file-util.service';
import { LearningPathMessageService } from 'src/app/services/learning-path-message.service';
import { LearningPathService } from 'src/app/services/learning-path.service';
import { LoadingService } from 'src/app/services/loading.service';
import { UserContextService } from 'src/app/services/user-context.service';

@Component({
  selector: 'app-learning-path-message',
  standalone: false,
  templateUrl: './learning-path-message.component.html',
  styleUrl: './learning-path-message.component.scss'
})
export class LearningPathMessageComponent implements OnInit {
  learningPathId!: string;

  learningPathFull: LearningPathFullModel | null = null;
  attachments: RawFileModel[] = [];

  formData: {
    to: PersonModel[];
    subject: string;
    body: string;
    files: File[];
  } = {
    to: [],
    subject: '',
    body: '',
    files: []
  };

  @ViewChild('recipientsForm') recipientsForm!: DxFormComponent;
  @ViewChild('contentForm') contentForm!: DxFormComponent;

  invitationTextTemplateType: TextTemplateTypeModel =
    TextTemplateTypeModel.AddParticipants;
  
  constructor(
    private route: ActivatedRoute,
    private learningPathService: LearningPathService,
    private loadingService: LoadingService,
    private router: Router,
    private fileUtilService: FileUtilService,
    private learningPathMessageService: LearningPathMessageService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    public userContext: UserContextService,
  ) {}

  ngOnInit(): void {
    this.learningPathId = this.route.snapshot.params['id'];
   
    this.loadingService.load(
      this.learningPathService.getLearningPathFull(this.learningPathId)
    ).subscribe((learningPathFull) => {
      this.learningPathFull = learningPathFull;

      this.formData.subject = learningPathFull.learningPath.emailSubject
        ? learningPathFull.learningPath.emailSubject
        : learningPathFull.learningPath.name;
      this.formData.body = learningPathFull.learningPath.emailBody
        ? learningPathFull.learningPath.emailBody
        : learningPathFull.learningPath.description || '';
    });
  }

  onInvitersChange(e: any) {
    this.formData.to = e.component.getSelectedNodeKeys();
  }

  onSelectedFileChange(files: File[]) {
    this.formData.files = files;
  }

  onBackClick(): void {
    this.router.navigate(['manage-learning-path', this.learningPathId]);
  }

  onSendClick() {
    const isRecipientsFormValid = this.recipientsForm.instance.validate().isValid;
    const isContentFormValid = this.contentForm.instance.validate().isValid;

     if (!this.formData.to.length) {
      this.toastrService.error(
        this.translateService.instant('LEARNING-PATHS.LEARNING-PATH-INVITE.ATTENDEES-REQUIRED')
      );
      return;
    }

    if (!isRecipientsFormValid || !isContentFormValid) {
      this.toastrService.error(
        this.translateService.instant('VALIDATIONS.VALUES-NOT-VALID')
      );
      return;
    }

    const attachmentsPromises = Promise.all(
      this.formData.files.map(file => {
        return this.fileUtilService.convertFileToRawFileModel(file);
      })
    );

    from(attachmentsPromises)
      .pipe(
        switchMap(attachments => {
          const model: TrainingMessageSendModel = {
            toPersonIds: this.formData.to
              ? this.formData.to.map(p => p.id)
              : [],
            subject: this.formData.subject,
            htmlBody: this.formData.body,
            attachments
          };
          
          return this.loadingService.load(
            this.learningPathMessageService.sendInvitation(
              this.learningPathId,
              model
            )
          );
        })
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translateService.instant(
              'LEARNING-PATHS.LEARNING-PATH-INVITE.INVITED-SUCCESSFULLY'
            )
          );
          this.router.navigate(['manage-learning-path', this.learningPathId]);
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          );
        }
      });
  }

  onTemplateContentChanged(content: string) {
    this.formData.body = content;
  }
}
